import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AnimatedSection from '../components/AnimatedSection';
import WellnessProductCard from '../components/WellnessProductCard';
import { wellnessProducts, wellnessCategories } from '../data/wellness-products';
import axios from 'axios';

const WellnessPage = () => {
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    location: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const filteredProducts = filter === 'all' 
    ? wellnessProducts 
    : wellnessProducts.filter(p => {
      // Map old category names to new ones
      const categoryMap = {
        'interieur': 'sauna-interieur',
        'exterieur': 'sauna-exterieur',
        'infrarouge': 'infrarouge'
      };
      return p.category === filter || p.category === categoryMap[filter];
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const endpoint = '/contact.php';

      const response = await axios.post(endpoint, {
        ...formData,
        subject: `Demande de devis - ${formData.projectType || 'Wellness'}`,
        message: `Type de projet: ${formData.projectType}\nLieu d'installation: ${formData.location}\n\nMessage:\n${formData.message}`
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });
      
      if (response.data && response.data.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          location: '',
          message: ''
        });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      let errorMessage = "Une erreur s'est produite. Veuillez réessayer ou nous contacter directement par email.";
      
      if (error.response) {
        if (error.response.status === 500) {
          errorMessage = "Erreur serveur. Veuillez réessayer plus tard ou nous contacter directement.";
        } else if (error.response.status === 404) {
          errorMessage = "Endpoint non trouvé. Veuillez vérifier la configuration de l'API.";
        }
      } else if (error.request) {
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion internet et réessayez.";
      }
      
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const scrollToDevis = (e) => {
    if (e) {
      e.preventDefault();
    }
    const element = document.getElementById('devis');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const location = useLocation();

  // SEO Meta tags
  useEffect(() => {
    document.title = 'Saunas & Wellness — Espace Client | Art du Chalet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Découvrez notre sélection de saunas et cabines infrarouges premium. Demandez votre devis personnalisé.');
    }
  }, []);

  // Scroll to devis section when arriving with hash #devis
  useEffect(() => {
    if (location.hash === '#devis') {
      // небольшая задержка, чтобы DOM успел отрисоваться
      const timeout = setTimeout(() => {
        scrollToDevis();
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div
        className="relative h-80 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1600)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-white">
            <div className="flex items-center text-sm mb-4">
              <Link to="/" className="opacity-90 hover:opacity-100">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>Saunas & Wellness</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4">Saunas & Wellness</h1>
            <p className="text-xl md:text-2xl opacity-90 max-w-3xl">
              Découvrez notre sélection de saunas et cabines infrarouges premium
            </p>
            <div className="mt-8">
              <Button
                onClick={scrollToDevis}
                className="bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold"
              >
                Demander un devis
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {wellnessCategories.map((category) => {
              // Map filter to match product categories
              const filterValue = category.id === 'all' ? 'all' : category.id;
              return (
                <Button
                  key={category.id}
                  variant={filter === filterValue ? 'default' : 'outline'}
                  onClick={() => setFilter(filterValue)}
                  className={filter === filterValue ? 'bg-[#c4d600] hover:bg-[#b0c200] text-gray-800' : ''}
                >
                  {category.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <AnimatedSection animation="fade-up">
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#c4d600] font-semibold mb-2">NOTRE SÉLECTION</p>
              <h2 className="text-4xl font-bold text-gray-800">Produits Wellness</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, index) => (
                <AnimatedSection 
                  key={product.id} 
                  animation="fade-up" 
                  delay={index * 100}
                  className="h-full"
                >
                  <WellnessProductCard product={product} />
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Quote Form Section */}
      <AnimatedSection animation="fade-up">
        <div id="devis" className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">
                Demandez votre devis personnalisé
              </h2>
              <p className="text-lg text-gray-600">
                Décrivez votre projet, nous vous recontactons sous 48h.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 bg-gray-50 rounded-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom / Prénom *
                  </label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full"
                    aria-required="true"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full"
                    aria-required="true"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <Input
                    id="phone"
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full"
                    aria-required="true"
                  />
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                    Type de projet *
                  </label>
                  <Select
                    name="projectType"
                    value={formData.projectType}
                    onValueChange={(value) => setFormData({ ...formData, projectType: value })}
                    required
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sélectionnez un type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sauna intérieur">Sauna intérieur</SelectItem>
                      <SelectItem value="Sauna extérieur">Sauna extérieur</SelectItem>
                      <SelectItem value="Cabine infrarouge">Cabine infrarouge</SelectItem>
                      <SelectItem value="Sur mesure">Sur mesure</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                  Lieu d'installation (ville ou adresse)
                </label>
                <Input
                  id="location"
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message / description du projet
                </label>
                <Textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full"
                />
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div 
                  className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800"
                  role="alert"
                  aria-live="polite"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Votre demande a été envoyée avec succès ! Nous vous recontacterons sous 48h.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div 
                  className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800"
                  role="alert"
                  aria-live="polite"
                >
                  <AlertCircle className="w-5 h-5" />
                  <span>Une erreur s'est produite. Veuillez réessayer ou nous contacter directement par email.</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#c4d600] hover:bg-[#b0c200] text-gray-800 font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer ma demande
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default WellnessPage;
